// src/pages/ProjectsPage.jsx
import React, { useEffect, useState } from "react";
import { Input, Button, Select, Empty, Avatar } from "antd";
import {
  AppstoreOutlined,
  ClockCircleOutlined,
  FilterOutlined,
  PlusOutlined,
  SearchOutlined,
  TableOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import ProjectCard from "./components/ProjectCard";
import ProjectFormDrawer from "./components/ProjectFormDrawer";
import useProjects from "hooks/projects/useProjects";
import { Spinner } from "react-bootstrap";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useIterations from "hooks/projects/useIterations";
import apiClient from "utils/apiClient";
import { PM_SERVICE_BASE_URL } from "constants";
import { useTags } from "hooks";
import ProjectsTableView from "./components/ProjectsTableView";
import RecentItems from "./components/RecentItems";
import ProjectDetailsDrawer from "./components/ProjectDetailsDrawer";
import { useAuth } from "contexts/AuthContext";

const Projects = () => {
  const { projects, loading, createProject, updateProject, deleteProject } =
    useProjects();
  const { iterationsByProject, loading: iterationsLoading } = useIterations();
  const { tags } = useTags();
  const { user } = useAuth();

  const [normalizedProjects, setNormalizedProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [activeTab, setActiveTab] = useState("kanban");
  const [selectedTags, setSelectedTags] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);

  const normalizeProjects = (projectsFromAPI) => {
    return projectsFromAPI.map((p) => ({
      id: p.id,
      name: p.name,
      code: p.code,
      description: p.description,
      ownerName: p.ownerName || "Unknown",
      // tags: p.tags ? p.tags.map((t) => t.name) : [],
      tags: p.tags,
      iterationCount: p.iterationCount,
      taskCount: p.taskCount || 0,
      createdAt: p.createdAt,
      updatedAt: p.updatedAt,
      targetPoints: p.targetPoints,
    }));
  };

  // debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    if (projects && projects.length > 0) {
      setNormalizedProjects(normalizeProjects(projects));
    }
  }, [projects]);

  const filteredProjects = normalizedProjects.filter((project) => {
    const matchesSearch = project.name
      .toLowerCase()
      .includes(debouncedSearch.toLowerCase());

    const matchesStatus = !statusFilter || project.status === statusFilter;

    const matchesTags =
      selectedTags.length === 0 ||
      project.tags.some((tag) => selectedTags.includes(tag.name));

    return matchesSearch && matchesStatus && matchesTags;
  });

  useEffect(() => {
    setIsFiltering(true);
    const timeout = setTimeout(() => setIsFiltering(false), 250);
    return () => clearTimeout(timeout);
  }, [search, statusFilter, selectedTags]);

  const openDetailsDrawer = (project) => {
    setEditingProject(project);
    setIsDetailsDrawerOpen(true);
  };

  const openEditDrawer = (project) => {
    setEditingProject(project);
    setIsDrawerOpen(true);
  };

  const handleProjectTagsUpdate = (projectId, updatedTags) => {
    setNormalizedProjects((prev) =>
      prev.map((project) =>
        project.id === projectId ? { ...project, tags: updatedTags } : project,
      ),
    );
  };

  const handleSubmitProject = async (values) => {
    const previousProjects = normalizedProjects;

    try {
      setDrawerLoading(true);
      const tagIds = values.tags || [];

      if (editingProject) {
        const updatedProject = {
          ...editingProject,
          name: values.name,
          description: values.description,
          ownerName: values.ownerName,
        };

        setNormalizedProjects((prev) =>
          prev.map((p) => (p.id === editingProject.id ? updatedProject : p)),
        );

        if (updatedProject) {
          await updateProject(editingProject.id, updatedProject);
        }
      } else {
        const tempId = Date.now();

        const fullTags = tags.filter((t) => tagIds.includes(t.id));

        const optimisticProject = {
          id: tempId,
          name: values.name,
          code: values.code || "",
          description: values.description,
          ownerName: values.ownerName || "Unknown",
          tags: fullTags,
          iterationCount: 0,
          taskCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          targetPoints: values.targetPoints || 0,
        };

        setNormalizedProjects((prev) => [optimisticProject, ...prev]);

        const created = await createProject({
          ...values,
          tagIds,
          creatorUserId: user?.id || null,
        });

        const normalizedCreated = normalizeProjects([created])[0];
        setNormalizedProjects((prev) =>
          prev.map((p) => (p.id === tempId ? normalizedCreated : p)),
        );
      }
    } catch (err) {
      setNormalizedProjects(previousProjects); // fallback when error occurs
      console.error("Failed to create/update project", err);
    } finally {
      setDrawerLoading(false);
      setIsDrawerOpen(false);
      setEditingProject(null);
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;

    const previousProjects = normalizedProjects;
    setDeleteLoading(true);

    try {
      setNormalizedProjects((prev) =>
        prev.filter((p) => p.id !== projectToDelete.id),
      );

      await deleteProject(projectToDelete.id);
    } catch (err) {
      setNormalizedProjects(previousProjects); // rollback
      console.error("Failed to delete project", err);
    } finally {
      setProjectToDelete(null);
      setDeleteLoading(false);
    }
  };

  if (loading || iterationsLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <div className="project-page">
      {/* Header */}
      <div className="project-header">
        <div className="project-title">
          <div className="project-icon">C</div>
          <div>
            <h1 style={{ margin: 0, padding: 0 }}>Projects</h1>
            <p style={{ margin: 0, padding: 0, fontSize: 12 }}>
              Manage and organize your team’s projects
            </p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
          <RecentItems />
          <Avatar.Group max={3}>
            <Avatar>AL</Avatar>
            <Avatar>DT</Avatar>
            <Avatar>MK</Avatar>
          </Avatar.Group>
        </div>
      </div>

      <div className="project-toolbar">
        <div className="custom-tabs">
          <div
            className={`tab-item ${activeTab === "kanban" ? "active" : ""}`}
            onClick={() => setActiveTab("kanban")}
          >
            <AppstoreOutlined />
            Kanban
          </div>

          <div
            className={`tab-item ${activeTab === "table" ? "active" : ""}`}
            onClick={() => setActiveTab("table")}
          >
            <TableOutlined />
            Table
          </div>
        </div>

        {/* Right - Controls */}
        <div className="toolbar-actions">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search..."
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
          />

          <Select
            mode="multiple"
            placeholder="Filter by tag"
            style={{ width: 220 }}
            value={selectedTags}
            onChange={setSelectedTags}
            options={tags.map((t) => ({
              value: t.name,
              label: t.name,
            }))}
            maxTagCount={1}
          />

          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="new-task-btn"
            onClick={() => setIsDrawerOpen(true)}
          >
            New Project
          </Button>
        </div>
      </div>

      {selectedTags.length > 0 && (
        <div className="active-filter">
          Filtering by tag:
          {selectedTags.map((tag) => (
            <span key={tag.id} className="active-filter-tag">
              {tag}
              <span
                className="remove-filter"
                onClick={() =>
                  setSelectedTags((prev) => prev.filter((t) => t !== tag))
                }
              >
                ✕
              </span>
            </span>
          ))}
          <span className="clear-all" onClick={() => setSelectedTags([])}>
            Clear all
          </span>
        </div>
      )}

      {/* Grid */}
      {activeTab === "kanban" && filteredProjects.length > 0 && (
        <div className={`projects-grid ${isFiltering ? "filtering" : ""}`}>
          {filteredProjects.map((project, index) => (
            <ProjectCard
              index={index}
              key={project.id}
              project={project}
              selectedTags={selectedTags}
              onTagClick={(tag) =>
                setSelectedTags((prev) =>
                  prev.includes(tag)
                    ? prev.filter((t) => t !== tag)
                    : [...prev, tag],
                )
              }
              onView={() => openDetailsDrawer(project)}
              onEdit={() => openEditDrawer(project)}
              onDelete={() => {
                setProjectToDelete(project);
                setDeleteModalOpen(true);
              }}
            />
          ))}
        </div>
      )}

      {/* Only show Empty in Kanban view */}
      {filteredProjects.length === 0 && (
        <div className="projects-empty">
          <Empty description="No projects found" />
        </div>
      )}

      {activeTab === "table" && filteredProjects.length > 0 && (
        <ProjectsTableView
          projects={filteredProjects}
          openDetailsDrawer={openDetailsDrawer}
          openEditDrawer={openEditDrawer}
          onDelete={(project) => {
            setProjectToDelete(project);
            setDeleteModalOpen(true);
          }}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
        />
      )}

      <ProjectDetailsDrawer
        visible={isDetailsDrawerOpen}
        onClose={() => setIsDetailsDrawerOpen(false)}
        project={editingProject}
        onEditProject={() => {}}
        onTagsUpdate={handleProjectTagsUpdate}
      />

      {/* drawer */}
      <ProjectFormDrawer
        open={isDrawerOpen}
        onClose={() => {
          if (!drawerLoading) {
            setIsDrawerOpen(false);
            setEditingProject(null);
          }
        }}
        mode={editingProject ? "edit" : "create"}
        initialValues={
          editingProject
            ? {
                ...editingProject,
                ownerName: editingProject.ownerName,
              }
            : {}
        }
        onSubmit={handleSubmitProject}
        loading={drawerLoading}
        onTagsUpdate={handleProjectTagsUpdate}
      />

      <ConfirmDeleteModal
        show={deleteModalOpen}
        setShow={setDeleteModalOpen}
        loading={deleteLoading}
        itemName="project"
        message={
          projectToDelete
            ? `Are you sure you want to delete "${projectToDelete.name}" project? This action cannot be undone.`
            : undefined
        }
        onConfirm={handleDeleteProject}
      />
    </div>
  );
};

export default Projects;
